import {
  alpha,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import { styled } from '@mui/material/styles';
import { formatDistance } from 'date-fns';
import { ar, fr, es, pt, de } from 'date-fns/locale';
import { useNavigate } from 'react-router';

import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PendingIcon from '@mui/icons-material/Pending';
import MessageIcon from '@mui/icons-material/Message';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import BlockIcon from '@mui/icons-material/Block';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RateReviewIcon from '@mui/icons-material/RateReview';

import api from 'src/redux/config';
import { socket } from 'src/socket/config';
import { collabSocket } from 'src/socket/collabSocket';
import { notificationsSocket } from 'src/socket/notificationsSocket';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAdminNotification,
  fetchCompanyActivityNotifications,
  fetchEmployeeNotifications,
  fetchStaffNotifications,
  markAdminNotificationAsViewed
} from 'src/redux/notificationSlice';
import { useNotificationAudio } from '../../../../../utils/useNotificationAudio';
import { registerWebPush } from 'src/utils/webPush';
import { useTranslation } from 'react-i18next';

// ─── constants ────────────────────────────────────────────────────────────────

const DATE_FNS_LOCALES = { ar, fr, es, pt, dt: de };

// Extract the dynamic name from each known backend message template using simple string ops
function extractNotifName(kind, message) {
  if (!message || typeof message !== 'string') return null;
  const m = message.trim();
  const after  = (marker) => { const i = m.indexOf(marker); return i !== -1 ? m.slice(i + marker.length).trim() : null; };
  const before = (marker) => { const i = m.toLowerCase().indexOf(marker.toLowerCase()); return i !== -1 ? m.slice(0, i).trim() : null; };
  switch (kind) {
    case 'claim':        return after(' by ') ?? after(' par ');
    case 'message':      return after(' by ') ?? after(' you ');
    case 'like':         return before(' liked ');
    case 'dislike':      return before(' disliked ');
    case 'favorite':     return before(' added ');
    case 'newApplication':   return before(' has applied ');
    case 'collaborationEnded': {
      const start = m.toLowerCase().indexOf(' with ');
      const end   = m.toLowerCase().indexOf(' has ended');
      return start !== -1 && end > start ? m.slice(start + 6, end).trim() : null;
    }
    case 'absenceAdded':   return before(' a signalé une absence');
    case 'diseaseAdded':   return before(' a soumis un arrêt maladie');
    case 'dayOffRequested':return before(' a demandé un congé');
    case 'endRequested':   return before(' a demandé la fin de contrat');
    case 'endContract':    return before(' a mis fin au contrat');
    case 'presenceScanned':return before(' a enregistré son');
    default: return null;
  }
}

const KIND_LABEL_KEYS = {
  // claims
  message: 'notifications.kind_message',
  claim: 'notifications.kind_claim',
  like: 'notifications.kind_like',
  dislike: 'notifications.kind_dislike',
  favorite: 'notifications.kind_favorite',
  // chat
  chatMessage: 'notifications.kind_chatMessage',
  chatFile: 'notifications.kind_chatFile',
  chatMessageFromCompany: 'notifications.kind_chatMessage',
  chatFileFromCompany: 'notifications.kind_chatFile',
  // collaboration lifecycle
  newApplication: 'notifications.kind_newApplication',
  Pending: 'notifications.kind_Pending',
  collaborationAccepted: 'notifications.kind_collaborationAccepted',
  collaborationRejected: 'notifications.kind_collaborationRejected',
  collaborationEnded: 'notifications.kind_collaborationEnded',
  collaborationRating: 'notifications.kind_collaborationRating',
  staffRating: 'notifications.kind_staffRating',
  staffAnnualReviewReminder: 'notifications.kind_staffAnnualReviewReminder',
  // automatic AI training reports
  trainingReportReady: 'notifications.kind_trainingReportReady',
  trainingReportSkipped: 'notifications.kind_trainingReportSkipped',
  staffMonthlyRatingReminder: 'notifications.kind_staffMonthlyRatingReminder',
  // absence
  absenceAdded: 'notifications.kind_absenceAdded',
  absenceApproved: 'notifications.kind_absenceApproved',
  absenceRejected: 'notifications.kind_absenceRejected',
  // disease
  diseaseAdded: 'notifications.kind_diseaseAdded',
  diseaseApproved: 'notifications.kind_diseaseApproved',
  diseaseRejected: 'notifications.kind_diseaseRejected',
  // day off
  dayOffRequested: 'notifications.kind_dayOffRequested',
  dayOffApproved: 'notifications.kind_dayOffApproved',
  dayOffRejected: 'notifications.kind_dayOffRejected',
  // end contract
  endRequested: 'notifications.kind_endRequested',
  endApproved: 'notifications.kind_endApproved',
  endRejected: 'notifications.kind_endRejected',
  endContract: 'notifications.kind_endContract',
  // presence
  presenceScanned: 'notifications.kind_presenceScanned'
};

const KIND_ICONS = {
  message: MessageIcon,
  claim: AssignmentIcon,
  like: ThumbUpIcon,
  dislike: ThumbDownIcon,
  favorite: FavoriteIcon,
  chatMessage: ChatIcon,
  chatFile: AttachFileIcon,
  chatMessageFromCompany: ChatIcon,
  chatFileFromCompany: AttachFileIcon,
  newApplication: PersonAddIcon,
  Pending: PendingIcon,
  collaborationAccepted: CheckCircleIcon,
  collaborationRejected: CancelIcon,
  collaborationEnded: BlockIcon,
  collaborationRating: StarIcon,
  staffRating: StarIcon,
  staffAnnualReviewReminder: StarIcon,
  trainingReportReady: AutoAwesomeIcon,
  trainingReportSkipped: ReportProblemIcon,
  staffMonthlyRatingReminder: RateReviewIcon,
  absenceAdded: EventBusyIcon,
  absenceApproved: CheckCircleIcon,
  absenceRejected: CancelIcon,
  diseaseAdded: LocalHospitalIcon,
  diseaseApproved: CheckCircleIcon,
  diseaseRejected: CancelIcon,
  dayOffRequested: BeachAccessIcon,
  dayOffApproved: CheckCircleIcon,
  dayOffRejected: CancelIcon,
  endRequested: ExitToAppIcon,
  endApproved: CheckCircleIcon,
  endRejected: CancelIcon,
  endContract: BlockIcon,
  presenceScanned: QrCodeScannerIcon
};

const KIND_COLORS = {
  message: '#18e68e',
  claim: '#ff9800',
  like: '#4caf50',
  dislike: '#f44336',
  favorite: '#e91e63',
  chatMessage: '#18e68e',
  chatFile: '#18e68e',
  newApplication: '#2196f3',
  Pending: '#ff9800',
  collaborationAccepted: '#4caf50',
  collaborationRejected: '#f44336',
  collaborationEnded: '#9e9e9e',
  collaborationRating: '#ff9800',
  staffRating: '#ffc107',
  staffAnnualReviewReminder: '#ff9800',
  trainingReportReady: '#18e68e',
  trainingReportSkipped: '#ff9800',
  staffMonthlyRatingReminder: '#ff9800',
  absenceAdded: '#ff9800',
  absenceApproved: '#4caf50',
  absenceRejected: '#f44336',
  diseaseAdded: '#f44336',
  diseaseApproved: '#4caf50',
  diseaseRejected: '#f44336',
  dayOffRequested: '#2196f3',
  dayOffApproved: '#4caf50',
  dayOffRejected: '#f44336',
  endRequested: '#9e9e9e',
  endApproved: '#4caf50',
  endRejected: '#f44336',
  endContract: '#f44336',
  presenceScanned: '#4caf50'
};

const UNREAD_BG = 'rgba(24, 230, 142, 0.08)';
const NOTIF_INITIAL_LIMIT = 20;
const NOTIF_LOAD_MORE_STEP = 10;

// ─── styled ───────────────────────────────────────────────────────────────────

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.9)};
        color: var(--app-ink);
        min-width: 16px;
        height: 16px;
        padding: 0;
        font-size: 0.65rem;

        @media (max-width: ${theme.breakpoints.values.sm}px) {
            min-width: 14px;
            height: 14px;
            font-size: 0.6rem;
        }

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

const ResponsiveIconButton = styled(IconButton)(
  ({ theme }) => `
    @media (max-width: ${theme.breakpoints.values.sm}px) {
        padding: 6px;

        & .MuiSvgIcon-root {
            font-size: 1.25rem;
        }
    }
`
);

// ─── Automatic AI training reports ────────────────────────────────────────────

const TRAINING_REPORT_KINDS = ['staff', 'team', 'annual', 'anniversary'];
const TRAINING_SKIP_REASONS = ['NotEnoughRatings', 'NotEnoughRatedStaff'];

// German is stored as 'dt', which is not a locale the browser knows.
const dateLocale = (lang) => (lang === 'dt' ? 'de' : lang);

const monthLabel = (monthKey, lang) => {
  const [year, month] = String(monthKey).split('-').map(Number);
  if (!year || !month) return '';
  return new Date(year, month - 1, 1).toLocaleDateString(dateLocale(lang), {
    month: 'long',
    year: 'numeric'
  });
};

const trainingPeriod = (notif, lang) =>
  notif.periodStart === notif.periodEnd || !notif.periodEnd
    ? monthLabel(notif.periodStart, lang)
    : `${monthLabel(notif.periodStart, lang)} – ${monthLabel(notif.periodEnd, lang)}`;

// The backend stores these notifications with structured fields (report type,
// period, reason, count): the sentence is rebuilt in the user's language. Without
// those fields (older payloads), the French `message` is shown as before.
function trainingNotifText(notif, t, lang) {
  if (notif.kind === 'staffMonthlyRatingReminder') {
    if (notif.pendingCount == null || !notif.periodStart) return null;
    return t('notifications.msg_staffMonthlyRatingReminder', {
      count: notif.pendingCount,
      month: monthLabel(notif.periodStart, lang)
    });
  }
  if (notif.kind !== 'trainingReportReady' && notif.kind !== 'trainingReportSkipped') {
    return null;
  }
  if (!TRAINING_REPORT_KINDS.includes(notif.trainingReportKind) || !notif.periodStart) {
    return null;
  }

  const staff = notif.senderData;
  const name =
    (staff ? `${staff.firstname || ''} ${staff.lastname || ''}`.trim() : '') ||
    t('notifications.training_someone');
  const report = t(`notifications.training_report_${notif.trainingReportKind}`, { name });
  const period = trainingPeriod(notif, lang);

  if (notif.kind === 'trainingReportReady') {
    return t('notifications.msg_trainingReportReady', { report, period });
  }
  const reason = TRAINING_SKIP_REASONS.includes(notif.skipReason)
    ? t(`notifications.training_skip_${notif.skipReason}`)
    : '';
  return t('notifications.msg_trainingReportSkipped', { report, period, reason });
}

// ─── NotificationItem ─────────────────────────────────────────────────────────

const NotificationItem = memo(function NotificationItem({
  notif,
  onClick,
  onDelete,
  isMobile,
  t,
  lang
}) {
  const [hovered, setHovered] = useState(false);
  const Icon = KIND_ICONS[notif.kind];
  const iconColor = KIND_COLORS[notif.kind] || '#18e68e';
  const showDelete = isMobile || hovered;
  const timeAgo = useMemo(
    () => formatDistance(new Date(notif?.createdAt || new Date()), new Date(), {
      addSuffix: true,
      locale: DATE_FNS_LOCALES[lang]
    }),
    [notif?.createdAt, lang]
  );

  return (
    <ListItemButton
      onClick={() => onClick(notif)}
      sx={{
        px: 2,
        py: 1.25,
        minWidth: isMobile ? 'auto' : 350,
        flexDirection: 'column',
        alignItems: 'stretch',
        backgroundColor: !notif.view ? UNREAD_BG : 'transparent',
        '&:hover': { color: '#000', '& .notif-delete': { color: '#000' } }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.9rem',
            color: 'inherit'
          }}
        >
          {Icon && <Icon sx={{ fontSize: 14, color: iconColor }} />}
          {KIND_LABEL_KEYS[notif.kind]
            ? t(KIND_LABEL_KEYS[notif.kind])
            : notif.kind}
          {!notif.view && (
            <FiberManualRecordIcon sx={{ fontSize: 9, color: 'var(--app-brand-text)' }} />
          )}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            variant="caption"
            sx={{
              textTransform: 'none',
              fontSize: '0.7rem',
              color: 'inherit',
              opacity: 0.6
            }}
          >
            {timeAgo}
          </Typography>
          {showDelete && (
            <IconButton
              aria-label="delete"
              size="small"
              onClick={(e) => onDelete(e, notif)}
              className="notif-delete"
              sx={{ p: 0.25, color: 'inherit' }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Typography
        component="span"
        variant="body2"
        sx={{
          fontSize: '0.82rem',
          color: 'inherit',
          opacity: 0.7,
          lineHeight: 1.4
        }}
      >
        {(() => {
          const training = trainingNotifText(notif, t, lang);
          if (training) return training;
          const msgKey = `notifications.msg_${notif.kind}`;
          const template = t(msgKey);
          if (template === msgKey) return notif?.message ?? '';
          const name = extractNotifName(notif.kind, notif?.message);
          if (name) return template.replace('{{name}}', name);
          if (!template.includes('{{')) return template;
          return notif?.message ?? '';
        })()}
      </Typography>
    </ListItemButton>
  );
});

// ─── HeaderNotifications ──────────────────────────────────────────────────────

function HeaderNotifications() {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [notifPermission, setNotifPermission] = useState(() =>
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [staffLimit, setStaffLimit] = useState(NOTIF_INITIAL_LIMIT);
  const [claimsLimit, setClaimsLimit] = useState(NOTIF_INITIAL_LIMIT);
  const [companyLimit, setCompanyLimit] = useState(NOTIF_INITIAL_LIMIT);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const notifications = useSelector(
    (state) => state.notification.notifications
  );
  const staffNotifications = useSelector(
    (state) => state.notification.staffNotifications
  );
  const companyNotifications = useSelector(
    (state) => state.notification.companyNotifications
  );
  const employeeNotifications = useSelector(
    (state) => state.notification.employeeNotifications
  );
  const currentUser = useSelector((state) => state.auth?.user || state.user);
  const currentUserEmail = currentUser?.profile?.email;
  const currentUserId = currentUser?.profile?._id || currentUser?.profile?.id;
  const companyId = currentUser?.profile?.company?._id;
  const role = useSelector((state) => state.user.role);
  const isEmployee = role === 'EMPLOYE';

  // The "Staff" tab shows different data depending on who's logged in:
  // admins see what their staff did (staffNotifications), while an
  // employee sees their own notifications (approvals, ratings, etc.).
  const staffTabData = isEmployee ? employeeNotifications : staffNotifications;

  const { playNotification } = useNotificationAudio();
  const playNotificationRef = useRef(playNotification);
  const currentUserIdRef = useRef(currentUserId);
  const lastCollabNotifRef = useRef(0);
  const staffFetchTimerRef = useRef(null);

  useEffect(() => {
    playNotificationRef.current = playNotification;
  }, [playNotification]);
  useEffect(() => {
    // Register for web push once per login — lets notifications reach this
    // browser even when the tab isn't open/focused, on top of the existing
    // live socket delivery. Silently no-ops if permission is denied or the
    // browser doesn't support it.
    if (currentUserId) registerWebPush();
  }, [currentUserId]);

  // Must run from a click: browsers only show the permission prompt in
  // response to a user gesture.
  const handleEnableNotifications = useCallback(async () => {
    await registerWebPush({ prompt: true });
    setNotifPermission(Notification.permission);
  }, []);

  useEffect(() => {
    // When a push notification is clicked and this tab is reused instead of
    // opening a new one (see notificationclick in firebase-messaging-sw.js),
    // the service worker posts the target route here instead of navigating
    // itself — a service worker has no access to the app's router.
    if (!('serviceWorker' in navigator)) return;
    const handleSwMessage = (event) => {
      if (event.data?.type === 'notificationClick' && event.data.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', handleSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleSwMessage);
  }, [navigate]);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  const staffUnread = useMemo(
    () => staffTabData.filter((n) => !n.view).length,
    [staffTabData]
  );
  const claimsUnread = useMemo(
    () => notifications.filter((n) => !n.view).length,
    [notifications]
  );
  const companyUnread = useMemo(
    () => companyNotifications.filter((n) => !n.view).length,
    [companyNotifications]
  );
  const unreadCount = staffUnread + claimsUnread + companyUnread;

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    dispatch(fetchAdminNotification());
    dispatch(fetchCompanyActivityNotifications());
    if (isEmployee) {
      dispatch(fetchEmployeeNotifications());
    } else {
      dispatch(fetchStaffNotifications());
    }

    const handleNotifMessage = async ({ message }) => {
      const senderEmail = message?.email || message?.claim?.email;
      if (senderEmail !== currentUserEmail) {
        await playNotificationRef.current();
      }
      dispatch(fetchAdminNotification());
    };

    const handleNewClaim = async ({ claim }) => {
      const claimEmail = claim?.added_by?.email || claim?.email;
      if (claimEmail !== currentUserEmail) {
        await playNotificationRef.current();
      }
      dispatch(fetchAdminNotification());
    };

    const handleClaimFeedback = async (data) => {
      const feedbackEmail = data?.result?.userEmail || data?.userEmail;
      if (feedbackEmail !== currentUserEmail) {
        await playNotificationRef.current();
      }
      dispatch(fetchAdminNotification());
    };

    const debouncedFetchStaff = () => {
      clearTimeout(staffFetchTimerRef.current);
      staffFetchTimerRef.current = setTimeout(() => {
        dispatch(fetchStaffNotifications());
      }, 1000);
    };

    const handleCollabNotification = async (payload) => {
      const notification = payload?.notification || payload;

      // Chat messages/files already trigger a sound + refresh via
      // handleCollabMessage (newMessage/newFile) — skip here to avoid
      // playing the notification sound twice for the same message.
      if (
        notification?.kind === 'chatMessage' ||
        notification?.kind === 'chatFile' ||
        notification?.kind === 'chatMessageFromCompany' ||
        notification?.kind === 'chatFileFromCompany'
      ) {
        return;
      }

      const now = Date.now();
      if (now - lastCollabNotifRef.current < 500) return;
      lastCollabNotifRef.current = now;

      if (notification?.sender !== currentUserIdRef.current) {
        await playNotificationRef.current();
      }
      debouncedFetchStaff();
    };

    const handleCollabMessage = async (payload) => {
      const data = payload.message || payload.file;
      const isSender =
        (data?.email && data.email === currentUserEmail) ||
        (data?.senderId && data.senderId === currentUserIdRef.current);
      if (!isSender) {
        await playNotificationRef.current();
        debouncedFetchStaff();
      }
    };

    // Generic real-time notifications pushed over the /notifications
    // namespace (room user:<currentUserId>) — e.g. favorites, staff ratings.
    const handleRealtimeNotification = async (payload) => {
      if (!payload) return;
      const senderId = payload.userId || payload.clientId;
      if (senderId !== currentUserIdRef.current) {
        await playNotificationRef.current();
      }

      if (payload.type === 'favorite') {
        dispatch(fetchCompanyActivityNotifications());
      } else if (payload.type === 'staffRating') {
        // Persisted only under the company (Company tab).
        if (!isEmployee) {
          dispatch(fetchCompanyActivityNotifications());
        }
      } else if (payload.type === 'staffMonthlyRating' && isEmployee) {
        // Persisted under the employee's own notification list (kind
        // 'staffMonthlyRating' — see getEmployeeCollabNotifs) — refresh it
        // immediately instead of waiting for the next mount.
        dispatch(fetchEmployeeNotifications());
      }
    };

    socket.on('notif-message', handleNotifMessage);
    socket.on('new-claim', handleNewClaim);
    socket.on('claim-feedback', handleClaimFeedback);

    collabSocket.on('notificationAdded', handleCollabNotification);
    collabSocket.on('notificationAccepted', handleCollabNotification);
    collabSocket.on('notificationRate', handleCollabNotification);
    collabSocket.on('diseaseMessage', handleCollabNotification);
    collabSocket.on('absenceMessage', handleCollabNotification);
    collabSocket.on('dayOffMessage', handleCollabNotification);
    collabSocket.on('newMessage', handleCollabMessage);
    collabSocket.on('newFile', handleCollabMessage);

    // Company-wide service alerts (waiter calls / room service). These are
    // emitted to the company room, so they must alert staff on ANY page —
    // the Service Requests screen only refreshes its own list. A sound is
    // played and, when the tab is in the background, an OS-level
    // notification is shown so staff working in another app still see the
    // alert. Permission is asked from the "Enable" banner in the popover
    // (browsers suppress prompts that aren't triggered by a click).
    const showOsNotification = (title, body) => {
      if (!('Notification' in window)) return;
      if (!document.hidden) return;
      if (Notification.permission !== 'granted') return;
      const osNotif = new Notification(title, { body });
      osNotif.onclick = () => {
        window.focus();
        osNotif.close();
      };
    };

    const handleWaiterCalled = async (payload) => {
      console.log('🔔 [socket] waiter:called received:', payload);
      await playNotificationRef.current();
      const message = payload?.tableNumber
        ? `${t('service_requests.new_waiter_call')} (${t('Table')} ${payload.tableNumber})`
        : t('service_requests.new_waiter_call');
      showOsNotification(t('service_requests.waiter_title'), message);
    };

    const handleRoomServiceRequested = async (payload) => {
      console.log('🛎️ [socket] room-service:requested received:', payload);
      await playNotificationRef.current();
      const message = payload?.roomNumber
        ? `${t('service_requests.new_room_service')} (${t('room_reservation.Room')} ${payload.roomNumber})`
        : t('service_requests.new_room_service');
      showOsNotification(t('service_requests.room_title'), message);
    };

    // Group chat messages — broadcast on the shared /collaboration namespace
    // under a per-company event name (see CollaborationGateway.handleGroupMessage).
    const groupMessageEvent = companyId ? `groupMessage:${companyId}` : null;
    const handleGroupMessage = async (msg) => {
      const isSender =
        (msg?.sender?.email && msg.sender.email === currentUserEmail) ||
        (msg?.sender?._id && msg.sender._id === currentUserIdRef.current);
      if (isSender) return;
      await playNotificationRef.current();
      const senderName = msg?.sender
        ? `${msg.sender.firstname || ''} ${msg.sender.lastname || ''}`.trim()
        : '';
      const body = msg?.text || (msg?.fileId ? t('Sent a file') : '');
      const fullMessage = senderName && body ? `${senderName}: ${body}` : senderName || body;
      showOsNotification(t('Group Chat'), fullMessage);
    };
    if (groupMessageEvent) collabSocket.on(groupMessageEvent, handleGroupMessage);

    notificationsSocket.on('notification:new', handleRealtimeNotification);
    notificationsSocket.on('waiter:called', handleWaiterCalled);
    notificationsSocket.on('room-service:requested', handleRoomServiceRequested);

    return () => {
      if (groupMessageEvent) collabSocket.off(groupMessageEvent, handleGroupMessage);
      socket.off('notif-message', handleNotifMessage);
      socket.off('new-claim', handleNewClaim);
      socket.off('claim-feedback', handleClaimFeedback);

      collabSocket.off('notificationAdded', handleCollabNotification);
      collabSocket.off('notificationAccepted', handleCollabNotification);
      collabSocket.off('notificationRate', handleCollabNotification);
      collabSocket.off('diseaseMessage', handleCollabNotification);
      collabSocket.off('absenceMessage', handleCollabNotification);
      collabSocket.off('dayOffMessage', handleCollabNotification);
      collabSocket.off('newMessage', handleCollabMessage);
      collabSocket.off('newFile', handleCollabMessage);
      notificationsSocket.off('notification:new', handleRealtimeNotification);
      notificationsSocket.off('waiter:called', handleWaiterCalled);
      notificationsSocket.off('room-service:requested', handleRoomServiceRequested);
      clearTimeout(staffFetchTimerRef.current);
    };
  }, [dispatch, currentUserEmail, isEmployee, t, companyId]);

  const handleNotificationClick = useCallback(
    (notif) => {
      dispatch(markAdminNotificationAsViewed(notif._id));

      const staffDetail = `/management/staff/detailsStaff/${notif.collaborationId}`;
      const routes = {
        // claims
        message: `/management/claims/details/${notif.claimId}`,
        claim: `/management/claims/detailsClaim/${notif.claimId}`,
        like: `/management/claims/detailsClaim/${notif.claimId}`,
        dislike: `/management/claims/detailsClaim/${notif.claimId}`,
        favorite: `/management/company/favorite-clients`,
        staffRating: `/management/staff-ratings`,
        staffAnnualReviewReminder: `/management/staff-ratings/admin?tab=annual`,
        trainingReportReady: `/management/staff-ratings/admin?tab=training`,
        trainingReportSkipped: `/management/staff-ratings/admin?tab=training`,
        staffMonthlyRatingReminder: `/management/staff-ratings/admin?tab=monthly`,
        groupMessage: `/management/staff/group-chat`,
        // chat
        chatMessage: `/management/staff/detailS/${notif.sender}`,
        chatFile: `/management/staff/detailS/${notif.sender}`,
        chatMessageFromCompany: `/management/staff/detailS/${notif.sender}`,
        chatFileFromCompany: `/management/staff/detailS/${notif.sender}`,
        // collaboration lifecycle
        newApplication: `/management/ReceivedCollab`,
        Pending: `/management/staff`,
        collaborationAccepted: staffDetail,
        collaborationRejected: `/management/SentCollab`,
        collaborationEnded: staffDetail,
        collaborationRating: staffDetail,
        // absence
        absenceAdded: staffDetail,
        absenceApproved: staffDetail,
        absenceRejected: staffDetail,
        // disease
        diseaseAdded: staffDetail,
        diseaseApproved: staffDetail,
        diseaseRejected: staffDetail,
        // day off
        dayOffRequested: staffDetail,
        dayOffApproved: staffDetail,
        dayOffRejected: staffDetail,
        // end contract
        endRequested: staffDetail,
        endApproved: staffDetail,
        endRejected: staffDetail,
        endContract: staffDetail,
        // presence
        presenceScanned: staffDetail
      };

      const route = routes[notif.kind];
      if (route) {
        navigate(route);
        if (
          notif.kind === 'message' ||
          notif.kind === 'like' ||
          notif.kind === 'dislike'
        ) {
          setTimeout(
            () => window.scrollTo({ top: 1300, behavior: 'smooth' }),
            500
          );
        }
      }
      handleClose();
    },
    [dispatch, navigate, handleClose]
  );

  const handleDeleteNotification = useCallback(
    async (e, notif) => {
      e.stopPropagation();
      try {
        await api.delete(`/notification/${notif._id}`);
        dispatch(fetchAdminNotification());
        dispatch(fetchCompanyActivityNotifications());
        if (isEmployee) {
          dispatch(fetchEmployeeNotifications());
        } else {
          dispatch(fetchStaffNotifications());
        }
      } catch (err) {
        console.error('Error deleting notification:', err);
      }
    },
    [dispatch, isEmployee]
  );

  const badgeSx = (isActive) => ({
    background: isActive ? '#000' : '#18e68e',
    color: isActive ? '#fff' : '#000',
    borderRadius: '10px',
    fontSize: '0.65rem',
    fontWeight: 700,
    px: 0.7,
    py: 0.1
  });

  return (
    <>
      <Tooltip arrow title={t('notifications.title')}>
        <ResponsiveIconButton color="primary" ref={ref} onClick={handleOpen}>
          <NotificationsBadge
            badgeContent={unreadCount}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </ResponsiveIconButton>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: isMobile ? '70vh' : '80vh',
              maxWidth: isMobile ? '90vw' : '400px'
            }
          }
        }}
      >
        <Box sx={{ p: isMobile ? 1.5 : 2, pb: 0 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'}>
            {t('notifications.title')}
          </Typography>
        </Box>

        {notifPermission === 'default' && (
          <Box
            sx={{
              mx: isMobile ? 1.5 : 2,
              mt: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {t('notifications.enable_browser_text')}
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleEnableNotifications}
            >
              {t('notifications.enable_browser_button')}
            </Button>
          </Box>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mt: 1,
            '& .MuiTab-root': { color: 'text.secondary' },
            '& .MuiTab-root.Mui-selected': { color: '#000' }
          }}
        >
          <Tab
            value={0}
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                {t('notifications.tab_staff')}
                {staffUnread > 0 && (
                  <Box component="span" sx={badgeSx(activeTab === 0)}>
                    {staffUnread}
                  </Box>
                )}
              </Box>
            }
          />
          <Tab
            value={1}
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                {t('notifications.tab_claims')}
                {claimsUnread > 0 && (
                  <Box component="span" sx={badgeSx(activeTab === 1)}>
                    {claimsUnread}
                  </Box>
                )}
              </Box>
            }
          />
          <Tab
            value={2}
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                {t('notifications.tab_company')}
                {companyUnread > 0 && (
                  <Box component="span" sx={badgeSx(activeTab === 2)}>
                    {companyUnread}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>

        {/* Staff tab */}
        {activeTab === 0 && (
          <List
            sx={{
              p: 0,
              overflowY: 'auto',
              maxHeight: isMobile ? '50vh' : '60vh'
            }}
          >
            {staffTabData.length > 0 ? (
              <>
                {staffTabData.slice(0, staffLimit).map((notif, index) => (
                  <NotificationItem
                    key={notif._id || `staff-${index}`}
                    notif={notif}
                    onClick={handleNotificationClick}
                    onDelete={handleDeleteNotification}
                    isMobile={isMobile}
                    t={t}
                    lang={lang}
                  />
                ))}
                {staffLimit < staffTabData.length && (
                  <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Button
                      size="small"
                      onClick={() => setStaffLimit((prev) => prev + NOTIF_LOAD_MORE_STEP)}
                    >
                      {t('notifications.show_more')}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('notifications.no_staff')}
                </Typography>
              </Box>
            )}
          </List>
        )}

        {/* Claims tab */}
        {activeTab === 1 && (
          <List
            sx={{
              p: 0,
              overflowY: 'auto',
              maxHeight: isMobile ? '50vh' : '60vh'
            }}
          >
            {notifications.length > 0 ? (
              <>
                {notifications.slice(0, claimsLimit).map((notif, index) => (
                  <NotificationItem
                    key={notif._id || index}
                    notif={notif}
                    onClick={handleNotificationClick}
                    onDelete={handleDeleteNotification}
                    isMobile={isMobile}
                    t={t}
                    lang={lang}
                  />
                ))}
                {claimsLimit < notifications.length && (
                  <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Button
                      size="small"
                      onClick={() => setClaimsLimit((prev) => prev + NOTIF_LOAD_MORE_STEP)}
                    >
                      {t('notifications.show_more')}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('notifications.no_claims')}
                </Typography>
              </Box>
            )}
          </List>
        )}

        {/* Company tab */}
        {activeTab === 2 && (
          <List
            sx={{
              p: 0,
              overflowY: 'auto',
              maxHeight: isMobile ? '50vh' : '60vh'
            }}
          >
            {companyNotifications.length > 0 ? (
              <>
                {companyNotifications.slice(0, companyLimit).map((notif, index) => (
                  <NotificationItem
                    key={notif._id || `company-${index}`}
                    notif={notif}
                    onClick={handleNotificationClick}
                    onDelete={handleDeleteNotification}
                    isMobile={isMobile}
                    t={t}
                    lang={lang}
                  />
                ))}
                {companyLimit < companyNotifications.length && (
                  <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Button
                      size="small"
                      onClick={() => setCompanyLimit((prev) => prev + NOTIF_LOAD_MORE_STEP)}
                    >
                      {t('notifications.show_more')}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('notifications.no_company')}
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Popover>
    </>
  );
}

export default HeaderNotifications;
